/**
 * orb-panel.ts — full-screen takeover, math-first agent, agent-edits-the-page.
 *
 * Talks to the existing /api/zeq/agent/page-chat endpoint, which runs the
 * 7-step compute protocol BEFORE calling the LLM. The math result is
 * always rendered above each LLM reply so the user sees the framework's
 * grounding — operators chain (KO42 + 1–3), masterSum, phase, Zeqond,
 * ZeqProof. Without that grounding it's just a chatbot.
 *
 * Agent-edits-the-page: when the LLM returns a full <!DOCTYPE html>...
 * </html> block, we extract it, POST to /api/chain/<slug>/site/upload,
 * reload the page. That's the whole "agent rewrites the page in real
 * time" loop — small and direct.
 *
 * Provider picker: pulls from /api/zeq/llm/providers (the framework's
 * existing BYOK list — OpenAI, Claude, Gemini, Groq, DeepSeek, Azure,
 * RunPod, Fireworks). Local Gemma is one option in that same list.
 * Storage via /api/zeq/llm/credentials.
 */

import { setState as setOrbState } from "../orb/orb-element.js";
import { startBridge, getActiveAgent, getSlug } from "./agent-rail-bridge.js";
import { pageChat, type PageChatTurn, type PageChatResponse } from "./page-chat-client.js";
import { pickChatLayout, currentViewport, PHI } from "../layout/fibonacci.js";

const PANEL_ID = "zeq-pulse-panel";
const STYLE_ID = "zeq-pulse-panel-style";

let mounted = false;
let panelEl: HTMLElement | null = null;
let bodyEl: HTMLElement | null = null;
let feedEl: HTMLElement | null = null;
let inputEl: HTMLTextAreaElement | null = null;
let sendBtn: HTMLButtonElement | null = null;
let agentLabelEl: HTMLElement | null = null;
let fibLabelEl: HTMLElement | null = null;

const history: PageChatTurn[] = [];
let inFlight = false;

// ─── Mount ─────────────────────────────────────────────────────────────

export function mountPanel(css: string | null = null): HTMLElement {
  if (mounted && panelEl) return panelEl;

  // App DNA — the framework's shared stylesheets. Loaded once per page.
  ensureLink("zeq-pulse-style-app", "/style.css", { locked: true });
  ensureLink("zeq-pulse-style-constants", "/zeq-constants.css", { locked: true });
  ensureLink(
    "zeq-pulse-fonts",
    "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap",
    { locked: true },
  );

  if (!document.getElementById(STYLE_ID)) {
    if (css) {
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.setAttribute("data-zeq-locked", "");
      style.textContent = css;
      document.head.appendChild(style);
    } else {
      const link = document.createElement("link");
      link.id = STYLE_ID;
      link.rel = "stylesheet";
      link.href = "/pulse/agent/orb-panel.css";
      link.setAttribute("data-zeq-locked", "");
      document.head.appendChild(link);
    }
  }

  const el = document.createElement("div");
  el.id = PANEL_ID;
  el.setAttribute("data-zeq-locked", "");
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-label", "Zeq Pulse — full screen");
  el.innerHTML = `
    <div class="panel-header">
      <span class="panel-status-dot" aria-hidden="true"></span>
      <span class="panel-agent" id="zeq-pulse-panel-agent">
        <span class="panel-agent-zid">Master</span><span class="panel-agent-name">slug-level agent</span>
      </span>
      <span class="panel-fib" id="zeq-pulse-panel-fib"></span>
      <button class="panel-provider" id="zeq-pulse-panel-cli" title="Switch to CLI mode — direct framework commands (Phase 4.C)">⌘ CLI</button>
      <button class="panel-provider" id="zeq-pulse-panel-provider" title="Pick AI provider (BYOK)">PROVIDER</button>
      <button class="panel-close" id="zeq-pulse-panel-close" aria-label="Close (Esc)">CLOSE</button>
    </div>
    <div class="panel-body" id="zeq-pulse-panel-body">
      <div class="panel-feed" id="zeq-pulse-panel-feed" role="log" aria-live="polite"></div>
      <form class="panel-composer" id="zeq-pulse-panel-composer">
        <textarea class="panel-input" id="zeq-pulse-panel-input"
                  placeholder="Tell the agent what to do — text, picture, video, contract, anything."
                  rows="1" maxlength="4000"></textarea>
        <button type="submit" class="panel-send" id="zeq-pulse-panel-send">SEND</button>
      </form>
    </div>
  `;
  document.body.appendChild(el);
  panelEl = el;
  bodyEl = el.querySelector("#zeq-pulse-panel-body") as HTMLElement;
  feedEl = el.querySelector("#zeq-pulse-panel-feed") as HTMLElement;
  inputEl = el.querySelector("#zeq-pulse-panel-input") as HTMLTextAreaElement;
  sendBtn = el.querySelector("#zeq-pulse-panel-send") as HTMLButtonElement;
  agentLabelEl = el.querySelector("#zeq-pulse-panel-agent") as HTMLElement;
  fibLabelEl = el.querySelector("#zeq-pulse-panel-fib") as HTMLElement;

  el.querySelector("#zeq-pulse-panel-close")?.addEventListener("click", close);
  el.querySelector("#zeq-pulse-panel-provider")?.addEventListener("click", openProviderPicker);
  // MI ↔ CLI toggle. The orb is the user's single entry point; this
  // button switches the takeover between Mathematically-Intelligent
  // chat (current panel — page-chat math-first → describe / build) and
  // the Web CLI (Phase 4.C, lives in shared/pulse/src/cli/web-cli.ts —
  // direct framework commands, root access).
  el.querySelector("#zeq-pulse-panel-cli")?.addEventListener("click", () => {
    void import("../cli/index.js").then((cli) => {
      close();           // hide MI takeover
      cli.openCli();     // show CLI takeover
    }).catch((err) => {
      appendMessage("error", `CLI unavailable: ${(err as Error)?.message ?? String(err)}`);
    });
  });
  el.querySelector("#zeq-pulse-panel-composer")?.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void send();
  });
  inputEl.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      void send();
    }
    autoGrow();
  });
  inputEl.addEventListener("input", autoGrow);
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && el.classList.contains("open")) close();
  });
  window.addEventListener("resize", applyFibLayout);

  startBridge();
  window.addEventListener("pulse:agent-changed", () => syncAgentLabel());
  syncAgentLabel();

  mounted = true;
  return el;
}

function ensureLink(id: string, href: string, opts: { locked?: boolean } = {}): void {
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  if (opts.locked) link.setAttribute("data-zeq-locked", "");
  document.head.appendChild(link);
}

function autoGrow(): void {
  if (!inputEl) return;
  inputEl.style.height = "auto";
  inputEl.style.height = Math.min(240, inputEl.scrollHeight) + "px";
}

// ─── Fibonacci layout ──────────────────────────────────────────────────

function applyFibLayout(): void {
  if (!bodyEl || !feedEl) return;
  const vp = currentViewport();
  const lay = pickChatLayout(vp);
  bodyEl.style.gridTemplateRows = `minmax(0, ${lay.rowSplit[1]}fr) auto`;
  feedEl.style.maxWidth = `${lay.feedMaxWidth}px`;
  const composer = bodyEl.querySelector(".panel-composer") as HTMLElement | null;
  if (composer) composer.style.maxWidth = `${lay.composerMaxWidth}px`;
  if (fibLabelEl) {
    fibLabelEl.textContent = `φ · fib[${lay.rowSplit[0]}/${lay.rowSplit[1]}] · ${Math.round(lay.feedMaxWidth)}px`;
  }
}

// ─── Open / close ──────────────────────────────────────────────────────

export function open(): void {
  if (!panelEl) return;
  panelEl.classList.add("open");
  applyFibLayout();
  setOrbState("listening");
  setTimeout(() => inputEl?.focus(), 60);
}
export function close(): void {
  if (!panelEl) return;
  panelEl.classList.remove("open");
  if (!inFlight) setOrbState("idle");
}
export function toggle(): void {
  if (!mounted) mountPanel();
  if (!panelEl) return;
  if (panelEl.classList.contains("open")) close();
  else open();
}

// ─── Agent label ───────────────────────────────────────────────────────

function syncAgentLabel(): void {
  if (!agentLabelEl) return;
  const agentId = getActiveAgent();
  if (!agentId) {
    agentLabelEl.innerHTML =
      `<span class="panel-agent-zid">Master</span>` +
      `<span class="panel-agent-name">slug-level agent</span>`;
    return;
  }
  const shortZid = agentId.length > 14 ? agentId.slice(0, 14) + "…" : agentId;
  agentLabelEl.innerHTML =
    `<span class="panel-agent-zid">${escapeHtml(shortZid)}</span>` +
    `<span class="panel-agent-name">selected</span>`;
}

// ─── Provider picker (uses framework's BYOK list) ──────────────────────

async function openProviderPicker(): Promise<void> {
  // Lazy-mount a tiny dropdown listing /api/zeq/llm/providers + a "local
  // gemma" option. Picking a provider opens a small input for the API
  // key (saved via /api/zeq/llm/credentials). For local, we trigger
  // WebLLM download. This is the ENTRY POINT to the framework's
  // existing BYOK system; we don't reimplement storage.
  const providersList = await safeFetchJson("/api/zeq/llm/providers");
  const providers: Array<{ id: string; name: string }> = providersList?.providers ?? [];
  const lines = ["select provider —"]
    .concat(providers.map((p, i) => `  ${i + 1}. ${p.name} (${p.id})`))
    .concat([`  ${providers.length + 1}. local Gemma (in-browser)`])
    .join("\n");
  appendMessage("system", lines);
  appendMessage("system", "Manage keys: open /state/admin/site/?slug=" + (getSlug() ?? "") + " and add a credential.");
}

async function safeFetchJson(url: string): Promise<any> {
  try {
    const r = await fetch(url, { credentials: "same-origin" });
    if (!r.ok) return null;
    return await r.json();
  } catch { return null; }
}

// ─── Send message → math-first page-chat → render grounding + reply ───

async function send(): Promise<void> {
  if (!inputEl || !sendBtn) return;
  const message = inputEl.value.trim();
  if (!message || inFlight) return;
  const slug = getSlug();
  if (!slug) {
    appendMessage("error", "Pulse needs a state-machine slug — open a /s/<slug>/ page.");
    return;
  }

  inFlight = true;
  sendBtn.disabled = true;
  appendMessage("user", message);
  history.push({ role: "user", content: message });
  inputEl.value = "";
  autoGrow();
  setOrbState("thinking");

  // Detect page-edit / build intent client-side so we can give the
  // visitor a heartbeat. The server's regex is the source of truth;
  // this client-side mirror is best-effort. When the visitor asks the
  // agent to BUILD/CHANGE/RENDER the page, the streaming code-gen
  // model can take 30-90s — without progress feedback the visitor
  // assumes the chat is hung. Shows a live "⚡ building · 0:12" cue
  // that ticks every 1000 ms until the response lands.
  const PAGE_EDIT_INTENT_RE_CLIENT =
    /\b(build|make|create|generate|render|turn\s+(?:this|the\s+page)\s+into|rebuild|rewrite|edit\s+(?:this|the\s+page)|change\s+(?:this|the\s+page)|replace\s+(?:this|the\s+page))\b/i;
  const isBuildTurn = PAGE_EDIT_INTENT_RE_CLIENT.test(message);

  let progressEl: HTMLElement | null = null;
  let progressTimer: number | null = null;
  if (isBuildTurn && feedEl) {
    progressEl = document.createElement("div");
    progressEl.className = "msg msg-status msg-building";
    progressEl.style.cssText =
      "font-family:'JetBrains Mono',ui-monospace,monospace;font-size:12px;color:#00ff88;background:rgba(0,255,136,0.06);border:1px solid rgba(0,255,136,0.22);border-radius:8px;padding:10px 14px;margin:8px 0;letter-spacing:0.3px;";
    progressEl.textContent = "⚡ building your application · 0:00 · streaming from the framework's code model";
    feedEl.appendChild(progressEl);
    scrollFeedToEnd();
    const startedAt = Date.now();
    progressTimer = window.setInterval(() => {
      if (!progressEl) return;
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      const m = Math.floor(elapsed / 60);
      const s = (elapsed % 60).toString().padStart(2, "0");
      progressEl.textContent = `⚡ building your application · ${m}:${s} · streaming from the framework's code model`;
    }, 500);
  }

  try {
    setOrbState("executing");
    const data = await pageChat({
      slug,
      message,
      history: history.slice(-20),
      agent_id: getActiveAgent() ?? undefined,
    });

    // Stop and remove the building heartbeat — the response landed.
    if (progressTimer !== null) { window.clearInterval(progressTimer); progressTimer = null; }
    if (progressEl && progressEl.parentNode) progressEl.parentNode.removeChild(progressEl);

    if (!data.ok) {
      appendMessage("error", data.error ?? "Unknown page-chat error.");
      setOrbState("rejected");
      return;
    }

    // Math-first grounding — ALWAYS shown above the LLM text.
    if (data.zeq) {
      // Pass the top-level zeqond AND the per-turn proof_digest so the
      // math card renders real numbers instead of em-dashes.
      renderMathBlock(data.zeq, data.zeq.proof_digest ?? data.zeqProof, data.zeqond);
    }

    // Tool calls — one card per tool the LLM invoked server-side.
    for (const tc of data.tools_used ?? []) {
      renderToolCall(tc);
    }

    // Detect agent page-edit: if the LLM produced a full HTML document,
    // upload it to the slug and reload. The Pulse becomes the editor.
    const editedHtml = extractFullHtmlBlock(data.response);
    if (editedHtml) {
      appendMessage("page-edit", "✓ agent rewrote your page · uploading + reloading…");
      void deployHtmlAndReload(slug, editedHtml);
    } else {
      appendMessage("assistant", data.response);
      history.push({ role: "assistant", content: data.response });
    }

    setOrbState("settling");
  } catch (err) {
    if (progressTimer !== null) { window.clearInterval(progressTimer); progressTimer = null; }
    if (progressEl && progressEl.parentNode) progressEl.parentNode.removeChild(progressEl);
    appendMessage("error", `network error: ${(err as Error)?.message ?? String(err)}`);
    setOrbState("rejected");
  } finally {
    inFlight = false;
    sendBtn.disabled = false;
    inputEl.focus();
  }
}

/** Pull a complete <!DOCTYPE html>…</html> block from the LLM's reply. */
function extractFullHtmlBlock(text: string): string | null {
  if (!text) return null;
  // Strip Markdown code fences first: ```html … ```
  const fenced = /```(?:html)?\s*([\s\S]*?)```/i.exec(text);
  const candidate = fenced ? fenced[1] : text;
  const m = /<!doctype html[\s\S]*?<\/html>/i.exec(candidate);
  return m ? m[0] : null;
}

async function deployHtmlAndReload(slug: string, html: string): Promise<void> {
  try {
    const r = await fetch(`/api/chain/${encodeURIComponent(slug)}/site/upload`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: "/",
        content_type: "text/html",
        bytes_b64: btoa(unescape(encodeURIComponent(html))),
        hosting_tier: "paid",
      }),
    });
    if (!r.ok) {
      const t = await r.text().catch(() => "");
      appendMessage("error", `upload failed (${r.status}): ${t.slice(0, 240)}`);
      return;
    }
    appendMessage("page-edit", "✓ deployed · reloading the page");
    setTimeout(() => location.reload(), 600);
  } catch (err) {
    appendMessage("error", `upload error: ${(err as Error)?.message ?? err}`);
  }
}

// ─── Render helpers ────────────────────────────────────────────────────

/**
 * Render the framework-grounding card above the LLM reply.
 *
 * Field-name fidelity matters here: the API emits the master sum as
 * `R_t` (not `master_sum`), the zeqond as a top-level field (not inside
 * `zeq`), and the proof hash as `proof_digest`. Reading the wrong names
 * causes the card to render em-dashes for real values, which has caused
 * confusion (the framework looked like it wasn't computing). This pulls
 * straight from the live response shape now.
 */
function renderMathBlock(
  zeq: NonNullable<PageChatResponse["zeq"]>,
  proof?: string,
  zeqondTop?: number,
): void {
  if (!feedEl) return;
  const div = document.createElement("div");
  div.className = "math-block";
  const ops = (zeq.operators ?? []).map((o) => o.id).join(" · ") || "—";
  const ms = typeof zeq.R_t === "number" ? zeq.R_t.toFixed(6) : "—";
  const ph = typeof zeq.phase === "number" ? zeq.phase.toFixed(6) : "—";
  const z  = typeof zeqondTop === "number" ? zeqondTop : "—";
  const pr = typeof zeq.precision === "number" ? zeq.precision.toFixed(4) : "—";
  const dom = zeq.domain ? ` · ${zeq.domain}` : "";
  div.innerHTML = `
    <span class="math-label">framework grounding · math first${escapeHtml(dom)}</span>
    <span class="math-row">operators · <span class="math-ops">${escapeHtml(ops)}</span></span><br/>
    <span class="math-row">R(t) · ${escapeHtml(ms)} · phase · ${escapeHtml(ph)} · Zeqond · ${escapeHtml(String(z))} · precision · ${escapeHtml(pr)}</span>
    ${proof ? `<br/><span class="math-proof">zeqProof · ${escapeHtml(String(proof).slice(0, 16))}…</span>` : ""}
  `;
  feedEl.appendChild(div);
  scrollFeedToEnd();
}

function renderToolCall(tc: { name: string; arguments: Record<string, unknown>; result: unknown }): void {
  if (!feedEl) return;
  const div = document.createElement("div");
  div.className = "tool-call";
  div.innerHTML = `
    <span class="tool-name">tool · ${escapeHtml(tc.name)}</span>
    <span>${escapeHtml(JSON.stringify(tc.arguments).slice(0, 240))}</span>
  `;
  feedEl.appendChild(div);
  scrollFeedToEnd();
}

function appendMessage(kind: "user" | "assistant" | "system" | "error" | "page-edit", text: string): HTMLElement {
  if (!feedEl) return document.createElement("div");
  const div = document.createElement("div");
  div.className = `msg ${kind}`;
  div.textContent = text;
  feedEl.appendChild(div);
  scrollFeedToEnd();
  return div;
}

function scrollFeedToEnd(): void {
  if (feedEl) feedEl.scrollTop = feedEl.scrollHeight;
}

function escapeHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, (m) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[m] as string),
  );
}

export const goldenRatio = PHI;
export const Panel = { mount: mountPanel, open, close, toggle };
export default Panel;
